import React from "react";
import { Modal, Button } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import { isEmpty } from "lodash";
import BulkUpdateTablesModal from "./BulkUpdateTablesModal";
import BlockoutSlotsModal from "./BlockoutSlotsModal";
import { RESERVATION_COLOR } from "../../constants";
import { formatConsecutiveDateRanges } from "./utils";

export default class TableSettingsOverviewModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      events: [],
      tableColors: {},
      experienceColors: {},
      showBulkUpdateModal: false,
      showBlockoutSlotsModal: false,
    };
    this.calendarRef = React.createRef();
  }

  componentDidMount() {
    this.generateEvents();
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.tables !== prevProps.tables ||
        this.props.experiences !== prevProps.experiences) &&
      this.props.show
    ) {
      this.generateEvents();
    }
    // Re-render calendar when modal shows to fix sizing issues
    if (this.props.show && !prevProps.show) {
      setTimeout(() => {
        if (this.calendarRef.current) {
          this.calendarRef.current.getApi().updateSize();
        }
      }, 200);
    }
  }

  generateEvents = () => {
    const { tables, experiences } = this.props;
    if (isEmpty(tables) && isEmpty(experiences)) return;

    const events = [];
    const tableColors = {};
    const experienceColors = {};
    const today = moment().startOf("day");
    const colorForExperiences = "#ffb74d"; // A distinct color for experiences

    tables.forEach((table, index) => {
      // Assign color: Use RESERVATION_COLOR for exclusive (PDR), and light green for standard
      const color = table.isExclusive ? RESERVATION_COLOR : "#1bc5bd";
      const backgroundColor = color;
      const maxDays = parseInt(table.max_reservable_days) || 20; // Default to 20 if not set

      tableColors[table._id] = {
        name: table.name,
        color,
        maxDays,
        isExclusive: table.isExclusive,
      };
      const blockoutDates = (table.blockout_dates || []).map((d) =>
        moment(d).format("YYYY-MM-DD"),
      );

      const endDate = today.clone().add(maxDays, "days");

      // Calculate reservable segments
      let currentStart = null;

      // Iterate loop from 0 to maxDays
      for (let i = 0; i <= maxDays; i++) {
        const currentDate = today.clone().add(i, "days");
        const dateStr = currentDate.format("YYYY-MM-DD");
        const isBlocked = blockoutDates.includes(dateStr);

        if (!isBlocked) {
          // If not blocked and no current segment started, start one
          if (!currentStart) {
            currentStart = currentDate.clone();
          }
        } else {
          // If blocked and we have a current segment, close it
          if (currentStart) {
            // End date in FullCalendar is exclusive for all-day events, so we use currentDate (which is the blocked day)
            // Example: Start Jan 1, Blocked Jan 3. Segment is Jan 1 - Jan 3 (exclusive), so covers Jan 1 and Jan 2.
            events.push({
              title: table.name,
              start: currentStart.format("YYYY-MM-DD"),
              end: currentDate.format("YYYY-MM-DD"), // Exclusive end
              backgroundColor: backgroundColor,
              borderColor: color,
              textColor: "#ffffff",
              allDay: true,
              extendedProps: {
                // tableId: table._id,
                type: "reservable",
              },
            });
            currentStart = null;
          }

          // Optionally add an event for the blockout date itself to make it explicit?
          // User asked to "cut" the event. So gaps are what they want.
          // But maybe a visual indicator of "Blocked" is helpful?
          // User requirement: "cut the color coded continues event if blockout days"
          // "label of blockout days" in legend implies we might want to show them?
          // If I just leave a gap, it's clear it's not reservable.
          // However, if multiple tables are blocked on the same day, it might just look empty.
          // For now, I will NOT render blockout events per table to avoid clutter,
          // as the request focuses on the "reservable" range being cut.
        }
      }

      // Close pending segment at the end of the range
      if (currentStart) {
        // The loop goes up to maxDays (inclusive).
        // If the last day (today + maxDays) is reservable, we need to close the segment.
        // The loop condition is i <= maxDays.
        // If i == maxDays is not blocked, currentStart is set/kept.
        // We finish the loop.
        // We need to push the segment. The end date should be (today + maxDays + 1 day) for exclusive end.
        events.push({
          title: table.name,
          start: currentStart.format("YYYY-MM-DD"),
          end: endDate.clone().add(1, "days").format("YYYY-MM-DD"),
          backgroundColor: backgroundColor,
          borderColor: color,
          textColor: "#ffffff",
          allDay: true,
          extendedProps: {
            type: "reservable",
          },
        });
      }
    });

    // Add blockout dates as a separate event type?
    // "I should also see a legend and the color coding with the label of blockout days"
    // This implies there IS a color for blockout days.
    // If I just cut the event, the "blockout" is just whitespace.
    // Maybe I should render a generic "Blocked" event if ALL tables are blocked? No, that's complex.
    // Or maybe render the blocked days in a specific color (e.g. gray) for that table?
    // Let's re-read: "cut the color coded continues event if blockout days".
    // This implies the colorful bar stops.
    // AND "label of blockout days ... in legend".
    // If the legend says "Red = Table A", "Gray = Blockout", then I should probably show Gray blocks for Blockout dates.
    // Let's add Gray blocks for blockout dates.

    tables.forEach((table) => {
      const blockoutDates = [
        ...new Set(
          (table.blockout_dates || []).map((d) =>
            moment(d).format("YYYY-MM-DD"),
          ),
        ),
      ].sort();

      const maxReservable = moment()
        .clone()
        .add(table.max_reservable_days || 20, "days");

      const validDates = blockoutDates.filter(
        (date) =>
          moment(date).isSameOrAfter(moment(), "day") &&
          moment(date).isSameOrBefore(maxReservable),
      );

      if (validDates.length === 0) return;

      let currentStart = validDates[0];
      let currentEnd = validDates[0];

      for (let i = 1; i < validDates.length; i++) {
        const date = validDates[i];
        if (moment(date).diff(moment(currentEnd), "days") === 1) {
          currentEnd = date;
        } else {
          events.push({
            title: `${table.name} (Blocked)`,
            start: currentStart,
            end: moment(currentEnd).add(1, "days").format("YYYY-MM-DD"),
            allDay: true,
            backgroundColor: "#E4E6EF",
            borderColor: "#E4E6EF",
            textColor: "#7E8299",
            extendedProps: {
              type: "blocked",
            },
          });
          currentStart = date;
          currentEnd = date;
        }
      }
      events.push({
        title: `${table.name} (Blocked)`,
        start: currentStart,
        end: moment(currentEnd).add(1, "days").format("YYYY-MM-DD"),
        allDay: true,
        backgroundColor: "#E4E6EF",
        borderColor: "#E4E6EF",
        textColor: "#7E8299",
        extendedProps: {
          type: "blocked",
        },
      });
    });

    if (!isEmpty(experiences)) {
      experiences.forEach((experience) => {
        experienceColors[experience._id] = {
          name: experience.name,
          color: colorForExperiences,
          available_days: experience.available_days || [],
        };

        const availableDays = (experience.available_days || [])
          .map((d) => moment(d).startOf("day"))
          .filter((d) => d.isSameOrAfter(today))
          .sort((a, b) => a.valueOf() - b.valueOf());

        if (availableDays.length > 0) {
          let rangeStart = availableDays[0];
          let rangeEnd = availableDays[0];

          for (let i = 1; i < availableDays.length; i++) {
            const date = availableDays[i];
            if (date.diff(rangeEnd, "days") === 1) {
              rangeEnd = date;
            } else {
              // Push the completed range
              events.push({
                title: experience.name,
                start: rangeStart.format("YYYY-MM-DD"),
                end: rangeEnd.clone().add(1, "day").format("YYYY-MM-DD"),
                allDay: true,
                backgroundColor: colorForExperiences,
                borderColor: colorForExperiences,
                textColor: "#ffffff",
                extendedProps: { type: "experience" },
              });
              // Start a new range
              rangeStart = date;
              rangeEnd = date;
            }
          }
          // Push the last or only range
          events.push({
            title: experience.name,
            start: rangeStart.format("YYYY-MM-DD"),
            end: rangeEnd.clone().add(1, "day").format("YYYY-MM-DD"),
            allDay: true,
            backgroundColor: colorForExperiences,
            borderColor: colorForExperiences,
            textColor: "#ffffff",
            extendedProps: { type: "experience" },
          });
        }
      });
    }

    this.setState({ events, tableColors, experienceColors });
  };

  render() {
    const { show, onHide } = this.props;
    const { events, tableColors, experienceColors } = this.state;

    return (
      <>
        <Modal show={show} onHide={onHide} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>Tables and Experiences Availability</Modal.Title>
            <div className="ml-auto mr-5">
              <Button
                variant="light-primary"
                size="sm"
                onClick={() => this.setState({ showBulkUpdateModal: true })}
                className="mr-2"
              >
                Update Availability
              </Button>
              <Button
                variant="light-warning"
                size="sm"
                onClick={() => this.setState({ showBlockoutSlotsModal: true })}
              >
                Blockout Slots
              </Button>
            </div>
          </Modal.Header>
          <Modal.Body className="p-0">
            <div className="d-flex flex-column h-100">
              <div className="alert alert-custom alert-light-info border-0 mb-0 py-4 px-10 rounded-0 d-flex align-items-center">
                <span className="svg-icon svg-icon-primary svg-icon-2x mr-4">
                  <i className="fas fa-info-circle text-info font-size-h4"></i>
                </span>
                <div
                  className="alert-text font-weight-boldest text-info text-uppercase font-size-xs"
                  style={{ letterSpacing: "0.5px" }}
                >
                  The colored segments below represent the dates when each table
                  or experience is available for online reservations.
                </div>
              </div>

              <div className="p-10">
                <style>
                  {`
                  .fc-button-primary {
                    background-color: ${RESERVATION_COLOR} !important;
                    border-color: ${RESERVATION_COLOR} !important;
                  }
                  .fc-button-primary:hover, .fc-button-primary:active, .fc-button-primary:focus {
                    background-color: ${RESERVATION_COLOR} !important;
                    border-color: ${RESERVATION_COLOR} !important;
                    box-shadow: none !important;
                  }
                `}
                </style>
                <FullCalendar
                  ref={this.calendarRef}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={events}
                  height="60vh"
                />

                {/* Legend */}
                <div className="mt-4 border-top pt-4">
                  <h5 className="font-weight-boldest mb-5">Settings Summary</h5>
                  <div className="table-responsive">
                    <table className="table table-head-custom table-vertical-center table-head-bg table-borderless">
                      <thead>
                        <tr className="text-left">
                          <th style={{ minWidth: "150px" }}>Name</th>
                          <th style={{ minWidth: "100px" }}>Type</th>

                          <th style={{ minWidth: "200px" }}>Blockout Dates</th>
                          <th style={{ minWidth: "200px" }}>Available Dates</th>
                          <th style={{ minWidth: "120px" }}>
                            Max Reservable Days
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Table Rows */}
                        {this.props.tables.map((table) => {
                          const tableInfo = tableColors[table._id] || {};
                          const today = moment().startOf("day");
                          const sortedDates = (table.blockout_dates || [])
                            .map((d) => moment(d))
                            .filter((d) => d.isSameOrAfter(today))
                            .sort((a, b) => a.valueOf() - b.valueOf());

                          const blockoutDates = formatConsecutiveDateRanges(
                            (table.blockout_dates || []).filter((d) =>
                              moment(d).isSameOrAfter(today),
                            ),
                          );
                          return (
                            <tr key={table._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <span
                                    className="bullet bullet-bar mr-3 h-20px w-4px"
                                    style={{ backgroundColor: tableInfo.color }}
                                  ></span>
                                  <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                                    {table.name}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {table.isExclusive ? (
                                  <span className="badge badge-light-primary font-weight-boldest px-3 py-1 font-size-xs text-uppercase">
                                    PDR
                                  </span>
                                ) : (
                                  <span className="badge badge-light-success font-weight-boldest px-3 py-1 font-size-xs text-uppercase">
                                    Standard
                                  </span>
                                )}
                              </td>

                              <td>
                                <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                                  {blockoutDates || "-"}
                                </span>
                              </td>
                              <td>-</td>
                              <td>
                                <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                                  {table.max_reservable_days || 20} days
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {/* Experience Rows */}
                        {Object.values(this.props.experiences).map(
                          (experience) => {
                            const experienceInfo =
                              experienceColors[experience._id] || {};

                            const availableDaysText =
                              formatConsecutiveDateRanges(
                                experience.available_days,
                              ) || "Rolling to Max Days";

                            return (
                              <tr key={experience._id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <span
                                      className="bullet bullet-bar mr-3 h-20px w-4px"
                                      style={{
                                        backgroundColor: experienceInfo.color,
                                      }}
                                    ></span>
                                    <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                                      {experience.name}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="badge badge-light-warning font-weight-boldest px-3 py-1 font-size-xs text-uppercase">
                                    Experience
                                  </span>
                                </td>
                                <td>-</td>
                                <td>
                                  <span className="text-dark-75 font-weight-bolder d-block font-size-lg">
                                    {availableDaysText || "-"}
                                  </span>
                                </td>
                                <td>-</td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
        {this.state.showBulkUpdateModal && (
          <BulkUpdateTablesModal
            show={this.state.showBulkUpdateModal}
            onHide={() => this.setState({ showBulkUpdateModal: false })}
            tables={this.props.tables}
            experiences={this.props.experiences}
            store={this.props.store}
            onSuccess={this.props.onSuccess}
          />
        )}
        {this.state.showBlockoutSlotsModal && (
          <BlockoutSlotsModal
            show={this.state.showBlockoutSlotsModal}
            onHide={() => this.setState({ showBlockoutSlotsModal: false })}
            tables={this.props.tables}
            experiences={this.props.experiences}
            store={this.props.store}
            onSuccess={() => {
              if (this.props.onSuccess) {
                this.props.onSuccess();
              }
              this.generateEvents();
            }}
          />
        )}
      </>
    );
  }
}
